import type { ReactNode } from "react";
import { MAINTENANCE_MESSAGE } from "@bing/shared";
import { Button } from "../components/Button";
import type { SavedOnlineGame } from "../game/online-resume";
import { fa } from "../i18n/fa";

/**
 * The MAINTENANCE screen: the platform is being updated and this user is not an admin.
 *
 * It is a MIRROR of a server decision, never a gate. Every api request and every game join is
 * refused server-side (503 `MAINTENANCE` / a rejected room join) whatever this renders — so
 * hiding or hacking this component buys nothing.
 *
 * The copy is the admin's own text from the panel, falling back to the shared default. It is
 * deliberately built from the existing `.screen` layout + design tokens rather than new
 * styling, so it reads as part of the app and follows the theme (light + dark) for free.
 *
 * It is NOT shown over a live game — see {@link shouldShowMaintenance}. A game already in
 * progress is allowed to finish; only new starts/joins are blocked.
 *
 * ⚠️ AND IT IS THE DOOR BACK TO ONE (operator, 2026-10-04: maintenance stops NEW games only; a
 * player with a live match can always get back to it). A player who RELOADS mid-match during
 * maintenance boots straight onto this screen — `/me` answers 503 — and used to have no way back
 * to the table they were seated at: the seat went to a bot at 60 s, both partners of a public حکم
 * table reloading ended it with rated wins for the opponents, a private host lost seat and crown.
 * So it offers THIS DEVICE'S saved ticket — the same one the lobby's «ادامه بازی» strip offers —
 * and the tap re-joins with `client.reconnect(token)`, which never runs the game-server's `onAuth`,
 * so maintenance cannot refuse it. A BUTTON, never an automatic return, exactly like the strip. A
 * private waiting room that has not dealt is offered as what it is (`pending`).
 */

/**
 * Should the maintenance screen replace the app right now?
 *
 * NOT while a game is in progress (`view === "game"`). That is the whole "live games finish"
 * rule made explicit: the game runs on the game-server's socket, which the api's maintenance
 * gate never touches, and the game-server refuses only NEW joins — so a player mid-game plays
 * on and sees this the moment they return to the lobby.
 *
 * Extracted as a pure predicate so the rule is testable on its own rather than buried in a
 * render condition.
 */
export function shouldShowMaintenance(
  maintenance: { active: boolean } | null,
  view: string,
): boolean {
  return Boolean(maintenance?.active) && view !== "game";
}

export function Maintenance({
  message,
  ticket,
  onReturn,
}: {
  message?: string;
  /** This device's live-table ticket (`loadOnlineGame()`), or null — see the note above. */
  ticket?: SavedOnlineGame | null;
  /** Re-join the ticket's table (`rejoinOnline`). No button without both. */
  onReturn?: () => void;
}): ReactNode {
  return (
    <div
      style={{
        height: "var(--app-h)",
        display: "grid",
        placeItems: "center",
        background: "var(--room-bg)",
        padding: 24,
        textAlign: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          alignItems: "center",
          maxWidth: 320,
        }}
      >
        <div aria-hidden style={{ fontSize: 44, lineHeight: 1 }}>
          🛠️
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 20,
            fontWeight: 800,
            color: "rgb(var(--c-on-surface))",
          }}
        >
          {fa.maintenance.title}
        </h1>
        <p
          style={{
            margin: 0,
            lineHeight: 1.8,
            color: "rgb(var(--c-on-surface-variant))",
          }}
        >
          {message?.trim() || MAINTENANCE_MESSAGE}
        </p>
        {ticket && onReturn ? (
          <>
            {/* A waiting room is not «a game in progress» — the strip's own line says so. */}
            {ticket.pending ? (
              <p style={{ margin: 0, fontSize: 13, color: "rgb(var(--c-on-surface-variant))" }}>
                {fa.lobby.tableNotStarted}
              </p>
            ) : null}
            <Button variant="primary" onClick={onReturn}>
              {ticket.pending ? fa.maintenance.returnToTable : fa.maintenance.returnToGame}
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}
